<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  order: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const parseDateTime = dateStr => {
  // dateStr comes as "2025-10-07 12:37:07.000000"
  const [datePart, timePart] = dateStr.split(' ');
  const time = timePart?.split('.')[0]; // "12:37:07"
  return { date: datePart, time };
};

const formatCurrency = (amount, currency) => {
  return new Intl.NumberFormat('en', {
    style: 'currency',
    currency: currency || 'SAR',
  }).format(amount);
};

const statusSlug = computed(() => props.order.status?.slug);

const orderStatus = computed(() => {
  if (!statusSlug.value) return '';
  const key = `CONVERSATION_SIDEBAR.WGPT_SALLA.STATUS.${statusSlug.value.toUpperCase()}`;
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  const translated = t(key);
  if (translated === key) return props.order.status?.name || statusSlug.value;
  return translated;
});

const STATUS_CLASSES = {
  completed: 'bg-n-teal-5 text-n-teal-12',
  delivered: 'bg-n-teal-5 text-n-teal-12',
  shipped: 'bg-n-teal-5 text-n-teal-12',
  in_progress: 'bg-n-blue-5 text-n-blue-12',
  delivering: 'bg-n-blue-5 text-n-blue-12',
  under_review: 'bg-n-amber-5 text-n-amber-12',
  payment_pending: 'bg-n-amber-5 text-n-amber-12',
  request_quote: 'bg-n-amber-5 text-n-amber-12',
  canceled: 'bg-n-ruby-5 text-n-ruby-12',
  deleted: 'bg-n-ruby-5 text-n-ruby-12',
  restored: 'bg-n-slate-5 text-n-slate-12',
  restoring: 'bg-n-slate-5 text-n-slate-12',
};

const statusClass = computed(
  () => STATUS_CLASSES[statusSlug.value] || 'bg-n-solid-3 text-n-slate-12'
);

const orderTotal = computed(() => {
  return formatCurrency(props.order.total?.amount, props.order.total?.currency);
});

const orderDateTime = computed(() => {
  if (!props.order.date?.date) return { date: '', time: '' };
  return parseDateTime(props.order.date.date);
});
</script>

<template>
  <div
    class="py-3 border-b border-n-weak last:border-b-0 flex flex-col gap-1.5"
  >
    <div class="flex justify-between items-center">
      <div class="font-medium flex">
        <a
          :href="order.url"
          target="_blank"
          rel="noopener noreferrer"
          class="hover:underline text-n-slate-12 cursor-pointer truncate"
        >
          {{
            $t('CONVERSATION_SIDEBAR.WGPT_SALLA.ORDER_ID', {
              id: order.reference_id,
            })
          }}
          <i class="i-lucide-external-link pl-5" />
        </a>
      </div>
      <div
        :class="statusClass"
        class="text-xs px-2 py-1 rounded capitalize truncate"
        :title="orderStatus"
      >
        {{ orderStatus }}
      </div>
    </div>
    <div class="flex justify-between items-end text-sm">
      <div class="flex flex-col text-n-slate-11">
        <span>{{ orderDateTime.date }}</span>
        <span class="text-xs">{{ orderDateTime.time }}</span>
      </div>
      <span class="text-n-slate-11">
        {{ orderTotal }}
      </span>
    </div>
  </div>
</template>
